import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from './api';
import Blanks from './pages/Blanks';
import Prints from './pages/Prints';
import Orders from './pages/Orders';
import Returns from './pages/Returns';
import Manifest from './pages/Manifest';
import CodTracking from './pages/CodTracking';
import Reviews from './pages/Reviews';
import FollowUps from './pages/FollowUps';
import Products from './pages/Products';
import Notifications from './pages/Notifications';
import DtfSheet from './pages/DtfSheet';
import Admin from './pages/Admin';
import Login from './pages/Login';
import { useAuth } from './auth';
import './App.css';

// Who is signed in, and the way out. Sits at the right of the nav so it is
// always visible without taking a tab's worth of room.
function WhoAmI() {
  const { user, signOut } = useAuth();
  if (!user) return null;
  return (
    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 9 }}>
      {user.picture && <img src={user.picture} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />}
      <span style={{ fontSize: 12, color: '#7d6a63' }}>
        {(user.name || user.email).split(' ')[0]}
        {user.role === 'owner' && <span style={{ color: '#93827a' }}> · owner</span>}
      </span>
      <button onClick={signOut} title="Sign out"
        style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
          background: '#ffffff', color: '#8c3242', border: '1px solid #e6dccc' }}>
        Sign out
      </button>
    </div>
  );
}

// The alert count rides in the nav so a critical problem is visible from
// every page, not only when you think to go looking for it.
function AlertBadge() {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    let live = true;
    const pull = async () => {
      try {
        const { data } = await axios.get(`${API}/api/alerts`);
        if (live) setCounts(data.counts || {});
      } catch (e) { /* the nav must never break over this */ }
    };
    pull();
    const t = setInterval(pull, 60000);
    return () => { live = false; clearInterval(t); };
  }, []);
  const n = (counts?.critical || 0) + (counts?.warning || 0);
  if (!n) return null;
  return (
    <span style={{
      marginLeft: 6, fontSize: 11, fontWeight: 700, borderRadius: 10, padding: '1px 7px',
      background: counts.critical ? '#b3261e' : '#7a6a12', color: '#fff'
    }}>{n}</span>
  );
}

function NavLink({ to, children }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link to={to} className={`navlink${isActive ? ' navlink-active' : ''}`}>
      {children}
    </Link>
  );
}

// The server only polls Shopify every hour once nobody is using the dashboard,
// so whatever is in the database on page load can be stale. Pull a fresh sync
// FIRST and hold the screen until it lands — the pages each fetch their data
// when they mount, so by rendering after this they read current numbers.
//
// A sync that fails must not lock you out: we fall through to the saved data
// and say so, rather than leaving a spinner up forever.
function useFreshData() {
  const [ready, setReady] = useState(false);
  const [staleWarning, setStaleWarning] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;   // StrictMode double-invokes effects in dev
    started.current = true;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/api/sync/now`, null, { timeout: 180000 });
        if (!cancelled && (res.data.failed || []).length) {
          setStaleWarning(`Some data could not be refreshed (${res.data.failed.join(', ')}).`);
        }
      } catch (e) {
        if (!cancelled) {
          setStaleWarning('Could not reach Shopify just now — showing the last saved data.');
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return { ready, staleWarning };
}

function Splash() {
  return (
    <div style={{
      minHeight: '100vh', background: '#f8f3eb', color: '#2e1f23',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{ textAlign: 'center' }}>
        <img src="/brand/logo-full.png" alt="The Think'th Goods Company"
          style={{ width: 150, height: 'auto', display: 'block', margin: '0 auto 16px' }} />
        <div style={{ color: '#7d6a63' }}>Fetching the latest from Shopify…</div>
      </div>
    </div>
  );
}

// Shown when someone reaches a tab they were not given.
function NoAccess() {
  return (
    <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
      <div style={{ fontSize: 17, marginBottom: 6 }}>Not your tab</div>
      <div style={{ color: '#93827a', fontSize: 14 }}>
        You do not have access to this page. Ask Dipansh if you need it.
      </div>
    </div>
  );
}

function App() {
  const auth = useAuth();
  const { ready, staleWarning } = useFreshData();

  // Sign in before anything is fetched — a signed-out visitor should see the
  // login screen, not a loading splash for data they will never be shown.
  if (auth.status === 'out') return <Login />;
  if (!ready || auth.status === 'loading') return <Splash />;

  // Sign-in gate. `disabled` is the state before Google sign-in is configured,
  // and it deliberately lets everything through — switching this on must never
  // be able to lock the dashboard out of itself.
  const can = auth.can;
  const home = [['prints', '/prints'], ['orders', '/orders'], ['returns', '/returns'], ['manifest', '/manifest'],
    ['cod', '/cod'], ['reviews', '/reviews'], ['followups', '/followups'], ['products', '/products'], ['alerts', '/alerts'], ['dtf', '/dtf']].find(([p]) => can(p))?.[1] || '/';

  return (
    <Router>
      <div style={{ minHeight: '100vh', background: '#f8f3eb', color: '#2e1f23' }}>
        <nav className="topnav">
          {/* The monogram alone in the bar: the full lockup is a mouthful at
              26px, and the eyes are the part people recognise. Cream, because the bar is wine. */}
          <span className="brand">
            <img src="/brand/monogram-light.png" alt="" />
            Think'th Goods
          </span>
          <div className="navlinks">
            {can('blanks') && <NavLink to="/">Blanks</NavLink>}
            {can('prints') && <NavLink to="/prints">Prints</NavLink>}
            {can('orders') && <NavLink to="/orders">Orders</NavLink>}
            {can('returns') && <NavLink to="/returns">Returns</NavLink>}
            {can('manifest') && <NavLink to="/manifest">Manifest</NavLink>}
            {can('cod') && <NavLink to="/cod">COD</NavLink>}
            {can('reviews') && <NavLink to="/reviews">Reviews</NavLink>}
            {can('followups') && <NavLink to="/followups">Follow-ups</NavLink>}
            {can('products') && <NavLink to="/products">Products</NavLink>}
            {can('alerts') && <NavLink to="/alerts">Alerts<AlertBadge /></NavLink>}
            {can('dtf') && <NavLink to="/dtf">DTF Sheet</NavLink>}
            {can('admin') && <NavLink to="/admin">Staff</NavLink>}
          </div>
          <WhoAmI />
        </nav>
        {staleWarning && (
          <div style={{
            background: '#fbeee5', color: '#8a5a2b', padding: '8px 16px',
            fontSize: 14, borderBottom: '1px solid #c05621'
          }}>
            {staleWarning}
          </div>
        )}
        <div className="content">
          {/* Typing a URL for a tab you were not given lands here, not on the
              page. The server refuses the data as well; this only keeps the
              screen honest. */}
          <Routes>
            {/* Someone without Blanks (e.g. only Reviews) lands on their first tab. */}
            <Route path="/" element={can('blanks') ? <Blanks /> : home !== '/' ? <Navigate to={home} replace /> : <NoAccess />} />
            <Route path="/prints" element={can('prints') ? <Prints /> : <NoAccess />} />
            <Route path="/orders" element={can('orders') ? <Orders /> : <NoAccess />} />
            <Route path="/returns" element={can('returns') ? <Returns /> : <NoAccess />} />
            <Route path="/manifest" element={can('manifest') ? <Manifest /> : <NoAccess />} />
            <Route path="/cod" element={can('cod') ? <CodTracking /> : <NoAccess />} />
            <Route path="/reviews" element={can('reviews') ? <Reviews /> : <NoAccess />} />
            <Route path="/followups" element={can('followups') ? <FollowUps /> : <NoAccess />} />
            <Route path="/products" element={can('products') ? <Products /> : <NoAccess />} />
            <Route path="/alerts" element={can('alerts') ? <Notifications /> : <NoAccess />} />
            <Route path="/dtf" element={can('dtf') ? <DtfSheet /> : <NoAccess />} />
            <Route path="/admin" element={can('admin') ? <Admin /> : <NoAccess />} />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;